import "core-js/modules/es.array.push.js";
import { startOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, subWeeks, subMonths, subYears, isSameDay, format } from 'date-fns';
import onClickOutside from '@/directives/onClickOutside';
const WEEK_OPTS = {
  weekStartsOn: 1
};
const PRESET_RANGES = {
  today: today => ({
    start: today,
    end: today
  }),
  yesterday: today => {
    const yesterday = subDays(today, 1);
    return {
      start: yesterday,
      end: yesterday
    };
  },
  thisWeek: today => ({
    start: startOfWeek(today, WEEK_OPTS),
    end: today
  }),
  lastWeek: today => {
    const lastWeek = subWeeks(today, 1);
    return {
      start: startOfWeek(lastWeek, WEEK_OPTS),
      end: endOfWeek(lastWeek, WEEK_OPTS)
    };
  },
  thisMonth: today => ({
    start: startOfMonth(today),
    end: today
  }),
  lastMonth: today => {
    const lastMonth = subMonths(today, 1);
    return {
      start: startOfMonth(lastMonth),
      end: endOfMonth(lastMonth)
    };
  },
  thisYear: today => ({
    start: startOfYear(today),
    end: today
  }),
  lastYear: today => {
    const lastYear = subYears(today, 1);
    return {
      start: startOfYear(lastYear),
      end: endOfYear(lastYear)
    };
  },
  allTime: (today, minDate) => ({
    start: minDate || null,
    end: today
  })
};
export default {
  name: 'BaseDateTimePicker',
  directives: {
    'click-outside': onClickOutside
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    },
    showQuickSelect: {
      type: Boolean,
      default: true
    },
    quickSelectOptions: {
      type: Array,
      default: () => [{
        label: 'Today',
        value: 'today'
      }, {
        label: 'Yesterday',
        value: 'yesterday'
      }, {
        label: 'This week',
        value: 'thisWeek'
      }, {
        label: 'Last week',
        value: 'lastWeek'
      }, {
        label: 'This month',
        value: 'thisMonth'
      }, {
        label: 'Last month',
        value: 'lastMonth'
      }, {
        label: 'This year',
        value: 'thisYear'
      }, {
        label: 'Last year',
        value: 'lastYear'
      }, {
        label: 'All time',
        value: 'allTime'
      }]
    },
    minDate: {
      type: [Date, String],
      default: () => new Date('2025-12-17')
    },
    trigger: {
      type: Boolean,
      default: false
    },
    triggerLabel: {
      type: String,
      default: ''
    },
    block: {
      type: Boolean,
      default: false
    },
    calendars: {
      type: Number,
      default: 2,
      validator: value => value === 1 || value === 2
    }
  },
  data() {
    return {
      currentMonths: [new Date(), new Date()],
      hoverDate: null,
      weekDays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'],
      isOpen: false,
      valueSnapshot: null
    };
  },
  computed: {
    minSelectableDate() {
      if (!this.minDate) {
        return null;
      }
      const date = this.minDate instanceof Date ? this.minDate : new Date(this.minDate);
      date.setHours(0, 0, 0, 0);
      return date;
    },
    startDate() {
      if (!this.value || !this.value.start) {
        return null;
      }
      return this.value.start instanceof Date ? this.value.start : new Date(this.value.start);
    },
    endDate() {
      if (!this.value || !this.value.end) {
        return null;
      }
      return this.value.end instanceof Date ? this.value.end : new Date(this.value.end);
    },
    resolvedTriggerLabel() {
      if (this.triggerLabel) {
        return this.triggerLabel;
      }
      if (this.startDate && this.endDate) {
        return `${format(this.startDate, 'MMM d')} - ${format(this.endDate, 'MMM d, yyyy')}`;
      }
      return 'Select date range';
    },
    selectedQuickOption() {
      if (!this.startDate && !this.endDate) {
        return null;
      }
      const matches = (resolved, current) => !resolved && !current || resolved && current && isSameDay(resolved, current);
      const match = this.quickSelectOptions.find(option => {
        const {
          start,
          end
        } = this.resolveOptionRange(option);
        return matches(start, this.startDate) && matches(end, this.endDate);
      });
      return match ? match.value : null;
    }
  },
  mounted() {
    this.initializeCalendars();
  },
  methods: {
    initializeCalendars() {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      this.currentMonths = [today, nextMonth];
    },
    getCalendarDates(calendarIndex) {
      const dates = [];
      const current = new Date(this.currentMonths[calendarIndex]);
      const year = current.getFullYear();
      const month = current.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      let startDay = firstDay.getDay();
      startDay = startDay === 0 ? 6 : startDay - 1;
      const prevMonthDays = new Date(year, month, 0).getDate();
      for (let i = startDay - 1; i >= 0; i--) {
        const date = new Date(year, month - 1, prevMonthDays - i);
        dates.push({
          date,
          day: date.getDate(),
          currentMonth: false,
          key: `prev-${prevMonthDays - i}`
        });
      }
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = new Date(year, month, i);
        dates.push({
          date,
          day: i,
          currentMonth: true,
          key: `current-${i}`
        });
      }
      const remainingDays = 42 - dates.length;
      for (let i = 1; i <= remainingDays; i++) {
        const date = new Date(year, month + 1, i);
        dates.push({
          date,
          day: i,
          currentMonth: false,
          key: `next-${i}`
        });
      }
      return dates;
    },
    isDateDisabled(date) {
      if (!this.minSelectableDate || !date) {
        return false;
      }
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);
      return checkDate < this.minSelectableDate;
    },
    selectDate(date) {
      if (this.isDateDisabled(date)) {
        return;
      }
      const currentStart = this.startDate;
      const currentEnd = this.endDate;
      if (!currentStart || currentStart && currentEnd) {
        this.$emit('input', {
          start: date,
          end: null
        });
      } else {
        if (date < currentStart) {
          this.$emit('input', {
            start: date,
            end: currentStart
          });
        } else {
          this.$emit('input', {
            start: currentStart,
            end: date
          });
        }
      }
    },
    resolveOptionRange(option) {
      const today = startOfDay(new Date());
      const min = this.minSelectableDate;
      let range = null;
      if (typeof option.range === 'function') {
        range = option.range({
          today,
          minDate: min
        });
      } else if (option.range && typeof option.range === 'object') {
        range = option.range;
      } else if (PRESET_RANGES[option.value]) {
        range = PRESET_RANGES[option.value](today, min);
      }
      if (!range) {
        return {
          start: null,
          end: null
        };
      }
      let start = range.start ? startOfDay(new Date(range.start)) : null;
      let end = range.end ? startOfDay(new Date(range.end)) : null;
      if (start && min && start < min) {
        start = new Date(min);
      }
      if (end && min && end < min) {
        end = new Date(min);
      }
      return {
        start,
        end
      };
    },
    selectQuickOption(option) {
      this.$emit('input', this.resolveOptionRange(option));
    },
    isSelected(date) {
      if (!date) {
        return false;
      }
      const time = date.getTime();
      return this.startDate && time === this.startDate.getTime() || this.endDate && time === this.endDate.getTime();
    },
    isInRange(date) {
      if (!this.startDate || !date) {
        return false;
      }
      const time = date.getTime();
      const endTime = this.endDate ? this.endDate.getTime() : this.hoverDate ? this.hoverDate.getTime() : null;
      if (!endTime) {
        return false;
      }
      return time > this.startDate.getTime() && time < endTime;
    },
    isRangeStart(date) {
      if (!this.startDate || !date) {
        return false;
      }
      return date.getTime() === this.startDate.getTime();
    },
    isRangeEnd(date) {
      if (!this.endDate || !date) {
        return false;
      }
      return date.getTime() === this.endDate.getTime();
    },
    formatDate(date) {
      if (!date) {
        return '';
      }
      return format(date, 'MM/dd/yyyy');
    },
    formatMonthYear(date) {
      return format(date, 'MMMM yyyy');
    },
    previousMonth(index) {
      const newDate = new Date(this.currentMonths[index]);
      newDate.setMonth(newDate.getMonth() - 1);
      this.$set(this.currentMonths, index, newDate);
    },
    nextMonth(index) {
      const newDate = new Date(this.currentMonths[index]);
      newDate.setMonth(newDate.getMonth() + 1);
      this.$set(this.currentMonths, index, newDate);
    },
    toUTCDateString(date) {
      if (!date) {
        return null;
      }
      return date.toISOString();
    },
    toggle() {
      if (this.isOpen) {
        this.isOpen = false;
        return;
      }
      this.valueSnapshot = {
        start: this.value ? this.value.start : null,
        end: this.value ? this.value.end : null
      };
      this.initializeCalendars();
      this.isOpen = true;
    },
    apply() {
      this.$emit('apply', {
        start: this.toUTCDateString(this.startDate),
        end: this.toUTCDateString(this.endDate)
      });
      if (this.trigger) {
        this.isOpen = false;
      }
    },
    cancel() {
      if (this.trigger) {
        if (!this.isOpen) {
          return;
        }
        if (this.valueSnapshot) {
          this.$emit('input', {
            start: this.valueSnapshot.start,
            end: this.valueSnapshot.end
          });
        }
        this.isOpen = false;
      }
      this.$emit('cancel');
    }
  }
};